import { useRef } from 'react';
import { Pause, Play } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { tip } from '../../publicRoute';

// PLAY AND WHERE THE CLIP IS, IN ONE ROUND CONTROL: the play button with the clip's progress
// drawn round it, from the top and clockwise, and a ball where the picture is right now.
//
// It was a play button and a bar across the width of the page under the picture: a whole row
// for a control a visitor uses to hold the frame where two parts meet. Round the button it
// takes the button's room, and the ball is still a handle: dragged round the ring, the picture
// follows, exactly as the bar's thumb did.
//
// THE RING DOES NOT WRAP. Dragged past the top, the clip stays at its end (or its start) rather
// than jumping to the other one: the top is where the movement starts and where it finishes,
// and a hand that overshoots it means "all the way", not "start again".
// 44 and not 56: the round control took a corner of the picture's width (Louis 2026-10-28), and 44
// is still the size a thumb can be relied on to hit.
const SIZE = 44;
const STROKE = 2.5;
const R = (SIZE - STROKE * 2 - 6) / 2;
const C = 2 * Math.PI * R;

export function RingTransport({ at, playing, onToggle, onSeek }: {
  // Where the clip is, 0 to 1.
  at: number;
  playing: boolean;
  onToggle: () => void;
  onSeek: (at: number) => void;
}) {
  const { t } = useLanguage();
  const ring = useRef<SVGSVGElement>(null);
  const last = useRef(at);
  last.current = at;
  const dragging = useRef(false);

  // THE FRACTION AT A POINTER, from the ring's top and clockwise — held at the end it is nearer
  // to where a hand crosses the top (see above).
  const fractionAt = (e: React.PointerEvent) => {
    const box = ring.current?.getBoundingClientRect();
    if (!box) return last.current;
    const x = e.clientX - (box.left + box.width / 2);
    const y = e.clientY - (box.top + box.height / 2);
    const turn = (Math.atan2(x, -y) / (2 * Math.PI) + 1) % 1;
    if (Math.abs(turn - last.current) > 0.5) return last.current > 0.5 ? 1 : 0;
    return turn;
  };

  const angle = at * 2 * Math.PI;
  const ball = { x: SIZE / 2 + R * Math.sin(angle), y: SIZE / 2 - R * Math.cos(angle) };
  return (
    // ON A WHITE DISC: it is drawn over the picture, beside the caption's arrows, and a pale track
    // on dark wood is no track at all.
    <div className="pointer-events-auto relative shrink-0 rounded-full bg-white/95 shadow-sm" style={{ width: SIZE, height: SIZE }}>
      {/* THE RING, and the hand on it: a press anywhere on it moves the clip there, and a drag
          carries it round. Under the button, so the middle stays play and pause. */}
      <svg
        ref={ring}
        width={SIZE}
        height={SIZE}
        role="slider"
        aria-label={t('notice.assembly.scrub')}
        aria-valuemin={0}
        aria-valuemax={1}
        aria-valuenow={Number(at.toFixed(3))}
        className="absolute inset-0 cursor-pointer touch-none"
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          // A press on the ring jumps there, whichever side of the top it lands.
          // From the ring's middle AS DRAWN, which ReaderScale may have scaled.
          const box = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - (box.left + box.width / 2);
          const y = e.clientY - (box.top + box.height / 2);
          onSeek((Math.atan2(x, -y) / (2 * Math.PI) + 1) % 1);
        }}
        onPointerMove={(e) => { if (dragging.current) onSeek(fractionAt(e)); }}
        onPointerUp={(e) => { dragging.current = false; e.currentTarget.releasePointerCapture(e.pointerId); }}
        onPointerCancel={() => { dragging.current = false; }}
      >
        <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" strokeWidth={STROKE} className="stroke-notice-blue/15" />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          fill="none"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${C * at} ${C}`}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          className="stroke-notice-blue"
        />
        <circle cx={ball.x} cy={ball.y} r={4.5} className="fill-notice-blue" />
      </svg>
      <button
        type="button"
        onClick={onToggle}
        aria-label={playing ? t('notice.clip.pause') : t('notice.clip.play')}
        title={tip(playing ? t('notice.clip.pause') : t('notice.clip.play'))}
        className="absolute left-1/2 top-1/2 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-notice-blue transition-colors hover:bg-notice-blue/5"
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

// AND THE SPEED IN THE SAME CONTROL, one white pill: the ring, then ×1 / ×2 at its side. Two
// controls side by side crowded the row the caption and its arrows stand in (Louis 2026-10-23).
// The published player's, and Phase 6's over the same picture (Louis 2026-10-28); without
// `onSpeed`, the ring alone.
export function RingAndSpeed({ at, playing, onToggle, onSeek, speed, onSpeed }: {
  at: number;
  playing: boolean;
  onToggle: () => void;
  onSeek: (at: number) => void;
  speed: number;
  onSpeed?: () => void;
}) {
  const { t } = useLanguage();
  const ring = <RingTransport at={at} playing={playing} onToggle={onToggle} onSeek={onSeek} />;
  if (!onSpeed) return ring;
  return (
    <div className="pointer-events-auto flex items-center rounded-full bg-white/95 pr-1 shadow-sm">
      {ring}
      <button
        type="button"
        onClick={onSpeed}
        aria-label={t('notice.clip.speed')}
        title={tip(t('notice.clip.speed'))}
        className="inline-flex h-8 min-w-8 shrink-0 items-center justify-center rounded-full px-1.5 text-xs font-bold tabular-nums text-notice-blue transition-colors hover:bg-notice-blue/5"
      >
        ×{speed}
      </button>
    </div>
  );
}
